// IMPORT REACT
import React, { useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// IMPORT CSS
import "../../theme/style.scss";

// IMPORT UI COMPONENTS
import {
  Box,
  Grid,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Popover,
  Pagination,
  Stack,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  styled,
  MenuProps,
  Menu,
  alpha,
  MenuItem,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import AddIcon from "@mui/icons-material/Add";
import { AlertType } from "../../services/models/AlertType";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";

// IMPORT SERVICE
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import GrowthChartItemSave from './GrowthChartItemSave';
import { DeleteMemberPhysicalGrowth, GetMemberPhysicalGrowthList } from "../../services/models/GrowthChart";
import { format, isValid } from "date-fns";
import { getCurrentFinancialYear } from "../../shared/CommonFunction";

import DateRangeIcon from "@mui/icons-material/DateRange";
import { bindMenu, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import { DateRange } from "react-date-range";

// PAGE SIZE
const PAGE_SIZE = 10;

// SCHEMA
interface Data {
  memberPhysicalGrowthId: number;
  patientId: number;
  opdId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

type Order = "desc" | "asc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  memberPhysicalGrowthId: string;
  patientId: string;
  opdId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  startDate: Date;
  endDate: Date;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  memberPhysicalGrowthId: "",
  patientId: "",
  opdId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  startDate: new Date(),
  endDate: new Date(),
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  memberPhysicalGrowthColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  memberPhysicalGrowthColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "10px !important",
    marginTop: theme.spacing(1),
    minWidth: 250,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px !important",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

interface IProps {
  opd: any;
  isForPopUp: boolean;
}

const GrowthChartItemList = React.memo(
  React.forwardRef<{ triggerChildFunction: () => void }, IProps>(
    (props: IProps, growthChartRef) => {
      // Props
      const { opd, isForPopUp } = props;

      // HOOKS AND CONTEXT
      const navigate = useNavigate();
      const edService = new EncryptDecryptService();
      const { userContext, updateUserContext } = useContext(PreferencesContext);

      const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
      const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
      const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
      const [page, setPage] = React.useState(0);
      const [page_Index, setPage_Index] = useState(1);
      const [value, setValue] = useState("");
      const [counData, setCounData] = useState(0);
      const [Curr_page, setCurrPage] = React.useState(1);
      const [totalCount, setTotalCount] = useState(0);
      const [pageStartIndex, setPageStartIndex] = useState(1);
      const [pageEndIndex, setPageEndIndex] = useState(1);

      // STATE FOR STORAGE
      const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
      const [resultData, setResultData] = useState<ResultData>(initResultData);
      const [selected, setSelected] = useState<readonly number[]>([]);
      const [param, setParam] = useState(parameters);
      const [memberPhysicalGrowthId, setMemberPhysicalGrowthId] = useState(0);
      const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
        null
      );
      const [openAddDialog, setOpenAddDialog] = useState({ open: false, memberPhysicalGrowthId: 0 });

      // STATES -- FOR ERROR HANDLING AND LOADING
      const [isLoading, setIsLoading] = useState<boolean>(true);

      // GETTING SEACH PARAMMS FOR PAGINATION
      function useQuery() {
        const { search } = useLocation();
        return useMemo(() => new URLSearchParams(search), [search]);
      }

      let todayDate = new Date();
      const daterangeArray = [
        {
          dateHeading: "Today",
          startDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(userContext?.financialYearDetail?.financialYearEndDate)
              : todayDate,
            "yyyy-MM-dd"
          ),
          endDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(userContext?.financialYearDetail?.financialYearEndDate)
              : todayDate,
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "Yesterday",
          startDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(
                new Date(
                  userContext?.financialYearDetail?.financialYearEndDate
                ).getTime() -
                1 * 24 * 60 * 60 * 1000
              )
              : new Date(new Date().getTime() - 1 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd"
          ),
          endDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(
                new Date(
                  userContext?.financialYearDetail?.financialYearEndDate
                ).getTime() -
                1 * 24 * 60 * 60 * 1000
              )
              : new Date(new Date().getTime() - 1 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "Last 15 days",
          startDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(
                new Date(
                  userContext?.financialYearDetail?.financialYearEndDate
                ).getTime() -
                14 * 24 * 60 * 60 * 1000
              )
              : new Date(new Date().getTime() - 14 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd"
          ),
          endDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(userContext?.financialYearDetail?.financialYearEndDate)
              : todayDate,
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "Last 30 days",
          startDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(
                new Date(
                  userContext?.financialYearDetail?.financialYearEndDate
                ).getTime() -
                29 * 24 * 60 * 60 * 1000
              )
              : new Date(new Date().getTime() - 29 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd"
          ),
          endDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(userContext?.financialYearDetail?.financialYearEndDate)
              : todayDate,
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "Last 90 days",
          startDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(
                new Date(
                  userContext?.financialYearDetail?.financialYearEndDate
                ).getTime() -
                89 * 24 * 60 * 60 * 1000
              )
              : new Date(new Date().getTime() - 89 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd"
          ),
          endDate: format(
            userContext?.financialYearDetail?.financialYear !==
              getCurrentFinancialYear()
              ? new Date(userContext?.financialYearDetail?.financialYearEndDate)
              : todayDate,
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "Current financial year",
          startDate: format(
            new Date(userContext?.financialYearDetail?.financialYearStartDate),
            "yyyy-MM-dd"
          ),
          endDate: format(
            new Date(userContext?.financialYearDetail?.financialYearEndDate),
            "yyyy-MM-dd"
          ),
        },
        {
          dateHeading: "All",
          startDate: null,
          endDate: null,
        },
      ];

      // GETTTING PARAMS
      let urlParams = useQuery();
      let ePage = urlParams.get("page");
      let encodedQuery = urlParams.get("q");
      let fd = urlParams.get("fd");
      let ed = urlParams.get("ed");

      let query = "";
      let curPage = 0;
      let startDate: string = "";
      let endDate: string = "";
      let dateRangeHeading: string = "";

      if (
        ePage != null &&
        ePage !== "" &&
        ePage !== undefined &&
        encodedQuery !== null &&
        encodedQuery !== undefined &&
        fd !== null &&
        fd !== undefined &&
        ed !== null &&
        ed !== undefined &&
        !isForPopUp
      ) {
        curPage = +ePage;

        if (fd !== "" && ed !== "") {
          if (isValid(new Date(fd)) && isValid(new Date(ed))) {
            startDate = fd;
            endDate = ed;
            let dateRange = daterangeArray?.find(
              (x) =>
                x.startDate === format(fd, "yyyy-MM-dd") &&
                x.endDate === format(ed, "yyyy-MM-dd")
            );
            if (dateRange) {
              dateRangeHeading = dateRange?.dateHeading;
            } else {
              dateRangeHeading =
                format(fd, "yyyy-MM-dd") + " TO " + format(ed, "yyyy-MM-dd");
            }
          }
        } else {
          dateRangeHeading = "All";
        }
        // ENCODE QUERY
        encodedQuery = encodeURIComponent(encodedQuery);

        // DECODE QUERY
        query = decodeURIComponent(encodedQuery);
      }

      // DATE RANGE
      const popupState = usePopupState({ variant: "popover", popupId: "demoMenu" });
      const [isCustomDate, setIsCustomDate] = React.useState(false);
      const [selectedDateRange, setDateRange] = useState<any>([
        {
          startDate: startDate ? new Date(startDate) : new Date(),
          endDate: endDate ? new Date(endDate) : new Date(),
          key: "selection",
        },
      ]);

      //
      useImperativeHandle(growthChartRef, () => ({
        triggerChildFunction: async (): Promise<void> => {
          setIsLoading(true);
          const res = await getGrowthList();
          setIsLoading(false);
          return res;
        },
      }));

      // HANDLE POPUP FOR EDIT AND DELETE
      const handleOpenMore = (
        event: React.MouseEvent<HTMLButtonElement>,
        memberPhysicalGrowthId: any
      ) => {
        setAnchorEl3(event.currentTarget);
        setMemberPhysicalGrowthId(memberPhysicalGrowthId);
      };

      // CLOSE POPUP
      const handleCloseMore = () => {
        setAnchorEl3(null);
      };

      // TABLE HEADER
      function EnhancedTableHead(props: EnhancedTableProps) {
        return (
          <TableHead className="table-header-color">
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                }}
                align="center"
              >
                Date
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="center"
              >
                Age
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="right"
              >
                Expected Height (cm)
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="right"
              >
                Weight (kg)
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="right"
              >
                Height (cm)
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="center"
              >
                Head Circumference (cm)
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="center"
              >
                BMI (kg/m²)
              </TableCell>
              <TableCell
                sx={{
                  display: {
                    xs: "none",
                    md: "table-cell",
                  },
                  fontWeight: "bold",
                }}
                align="center"
              >
                OPD
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
        );
      }

      // SORTING TABLE
      function stableSort<T>(
        array: readonly T[],
        comparator: (a: T, b: T) => number
      ) {
        const stabilizedThis = array?.map(
          (el, index) => [el, index] as [T, number]
        );
        stabilizedThis?.sort((a, b) => {
          const order = comparator(a[0], b[0]);
          if (order !== 0) {
            return order;
          }
          return a[1] - b[1];
        });
        return stabilizedThis?.map((el) => el[0]);
      }

      // DESCENDING COMPERISION
      function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        if (b[orderBy] < a[orderBy]) {
          return -1;
        }
        if (b[orderBy] > a[orderBy]) {
          return 1;
        }
        return 0;
      }

      // GET COMPERISION
      function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key
      ): (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string }
      ) => number {
        return order === "desc"
          ? (a, b) => descendingComparator(a, b, orderBy)
          : (a, b) => -descendingComparator(a, b, orderBy);
      }

      // GET LIST OF CONSUMER
      const getGrowthList = async () => {
        try {
          let params = new URLSearchParams();
          params.append("patientId", opd.patientId?.toString());
          params.append("pageIndex", param.pageIndex?.toString());
          params.append("pageSize", param.pageSize?.toString());
          params.append("sortColumn", param.sortColumn);
          params.append("sortDirection", param.sortDirection);

          if (param?.startDate) {
            params.append("startDate", format(param.startDate, "yyyy-MM-dd"));
          }
          if (param?.endDate) {
            params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
          }

          const result: any = await GetMemberPhysicalGrowthList(params);
          if (result?.data?.data?.memberPhysicalGrowthColl) {
            const resultData: ResultData = result?.data?.data;
            let memberPhysicalGrowthColl = resultData?.memberPhysicalGrowthColl || [];
            let dataCount = resultData?.totalCount || 0;
            let showDataCount = Math.ceil(dataCount / param.pageSize);
            setCounData(showDataCount);
            setResultData(resultData);
            let startIndex = (page_Index - 1) * param.pageSize + 1;
            let endIndex =
              (page_Index - 1) * param.pageSize + (memberPhysicalGrowthColl?.length || 0);
            setPageStartIndex(startIndex);
            setPageEndIndex(endIndex);
            setTotalCount(dataCount);
          } else {
          }
          setIsLoading(false);
        } catch (e) {
          setIsLoading(false);
          console.log("err:", e);
        }
      };

      // ON CHANGE PAGE
      const handleChangePageNew = (
        event: React.ChangeEvent<unknown>,
        value: number
      ) => {
        if (Curr_page !== value) {
          setCurrPage(value);
          setPage_Index(value);
          setParam({ ...param, pageIndex: value });

          // navigate(
          //   `?page=${value}&fd=${param.startDate}&ed=${param.endDate}&q=${encodedQuery}`
          // );
        }
      };

      // ON SERCH
      function searchValue(val: any) {
        if (val === "") {
          setValue("");
        }
        setPage_Index(1);
        setParam({ ...param, searchValue: val.toString(), pageIndex: 1 });
      }

      // OPEN POPUP OF ADD PHYSICAL GROWTH
      const handleOpenAddDialog = (memberPhysicalGrowthId: number) => {
        setOpenAddDialog({ memberPhysicalGrowthId: memberPhysicalGrowthId, open: true })
      };

      // CLOSE POPUP OF ADD PHYSICAL GROWTH
      const handleCloseAddDialog = () => {
        setOpenAddDialog({ memberPhysicalGrowthId: 0, open: false });
        handleCloseMore();
        getGrowthList();
      }

      // HANDLE UPDATE
      const handleUpdate = (memberPhysicalGrowthId: any) => {
        let encryptitemtId: any = edService.encrypt(memberPhysicalGrowthId);
        if (isForPopUp) {
          handleOpenAddDialog(memberPhysicalGrowthId);
        } else {
          navigate(
            {
              pathname: "growth-chart-item-save",
              search: `?q=${encodeURIComponent(encryptitemtId)}`,
            },
            {
              state: {
                title: " Edit",
                button: "Update",
                memberPhysicalGrowthId: memberPhysicalGrowthId,
              },
            }
          );
        }
      };

      // HANDLE DELETE WARNING POPUP
      const handleDeleteDilog = (value) => {
        setOpenDeleteDilog(value);
      };

      // HANDLE DELETE
      async function deleteRow(itemtIds: any) {
        try {
          setAnchorEl3(null);
          handleDeleteDilog(false);
          let DeleteData = {
            memberPhysicalGrowthIds: itemtIds?.toString(),
            // familyId: itemtIds?.toString(),
            // familyMemberId: itemtIds?.toString(),
            isDeleted: true,
            dataManagedBy: userContext?.loginDetail?.userId,
          };
          const result: any = await DeleteMemberPhysicalGrowth(DeleteData);
          if (result) {
            await getGrowthList();
            updateUserContext({
              ...userContext,
              isAlert: true,
              alertMessage: result.message,
              alertType: AlertType.Success,
            });
          } else {
            updateUserContext({
              ...userContext,
              isAlert: true,
              alertMessage: result.message,
              alertType: AlertType.Error,
            });
          }
        } catch (e: any) {
          updateUserContext({
            ...userContext,
            isAlert: true,
            alertMessage:
              typeof e === "string" ? (e as string) : (e?.message as string),
            alertType: AlertType.Error,
          });
        }
      }

      // HANDLE CHECK ROW
      const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
          const newSelecteds = resultData?.memberPhysicalGrowthColl?.map(
            (n: any) => n?.memberPhysicalGrowthId || 0
          );
          setSelected(newSelecteds);
          return;
        }
        setSelected([]);
      };

      // Update the fetchData function to only fetch GrowthList
      const fetchData = async () => {
        try {
          setIsLoading(true);
          await getGrowthList();
        } catch {
          setIsLoading(false);
        }
      };

      // USEEFFECTS
      useEffect(() => {
        fetchData();
      }, [param]);

      return (
        <>
          <Box>
            <Box className="main-container" mb={5}>
              <Grid container className="list-container">
                <Grid item xs={6}>
                  <Typography className="bold-600">
                    Growth Chart Summary
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  {opd?.isEditable && <Box className="page-header-icon">
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={(e) => {
                        handleOpenAddDialog(0);
                      }}
                      size="medium"
                      startIcon={<AddIcon />}
                    >
                      Add
                    </Button>
                  </Box>}
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} mt={2}>
                  <TableContainer>
                    <Table>
                      <EnhancedTableHead
                        numSelected={resultData?.memberPhysicalGrowthColl?.length || 0}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleCheckSelectAll}
                        rowCount={resultData?.memberPhysicalGrowthColl?.length}
                      />
                      <TableBody>
                        {isLoading ? (
                          Array(skeletonRowCount)
                            ?.fill(0)
                            ?.map((skeletonRow, index) => {
                              return (
                                <TableRow key={"skeleton-row" + index?.toString()}>
                                  <TableCell colSpan={6}>
                                    <SkeletonWrapper
                                      isLoading={isLoading}
                                      className="skeleton-input"
                                    >
                                      <span></span>
                                    </SkeletonWrapper>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                        ) : (
                          <>
                            {stableSort(
                              resultData?.memberPhysicalGrowthColl,
                              getComparator(order, orderBy)
                            )
                              .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                              )
                              .map((item: any, index: any) => {
                                return (
                                  <TableRow key={item?.memberPhysicalGrowthId?.toString()}>
                                    <TableCell
                                      sx={{
                                        textAlign: "center",
                                      }}
                                    >
                                      {/* {!isForPopUp ?
                                        <Link
                                          className="text-link"
                                          to={{
                                            pathname: "growth-chart-item-save",
                                            search: `?q=${encodeURIComponent(
                                              edService.encrypt(item?.memberPhysicalGrowthId || 0) ||
                                              0
                                            )}`,
                                          }}
                                        >
                                          {format(
                                            new Date(item?.entryDate),
                                            "dd-MM-yyyy"
                                          )}
                                        </Link> :
                                        <Typography
                                          className="text-link link-cursor"
                                          onClick={() => handleUpdate(item?.memberPhysicalGrowthId)}
                                        >
                                          {format(
                                            new Date(item?.entryDate),
                                            "dd-MM-yyyy"
                                          )}
                                        </Typography>
                                      } */}
                                      {
                                        opd?.isEditable ? (
                                          !isForPopUp ? (
                                            <Link
                                              className="text-link"
                                              to={{
                                                pathname: "growth-chart-item-save",
                                                search: `?q=${encodeURIComponent(
                                                  edService.encrypt(item?.memberPhysicalGrowthId || 0) || 0
                                                )}`,
                                              }}
                                            >
                                              {format(new Date(item?.entryDate), "dd-MM-yyyy")}
                                            </Link>
                                          ) : (
                                            <Typography
                                              className="text-link link-cursor"
                                              onClick={() => handleUpdate(item?.memberPhysicalGrowthId)}
                                            >
                                              {format(new Date(item?.entryDate), "dd-MM-yyyy")}
                                            </Typography>
                                          )
                                        ) : (
                                          <Typography className="text-link">
                                            {format(new Date(item?.entryDate), "dd-MM-yyyy")}
                                          </Typography>
                                        )
                                      }

                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "center",
                                        },
                                      }}
                                    >
                                      {item?.familyMemberAge ? `${item?.familyMemberAge}` : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "right",
                                        },
                                      }}
                                    >
                                      {item?.expectedHeight ? item.expectedHeight.toFixed(2) : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "right",
                                        },
                                      }}
                                    >
                                      {item.weight ? `${item?.weight}` : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "right",
                                        },
                                      }}
                                    >
                                      {item?.height ? `${item?.height}` : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "center",
                                        },
                                      }}
                                    >
                                      {item.headCircumference ? `${item?.headCircumference}` : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "center",
                                        },
                                      }}
                                    >
                                      {item.bmi ? `${item?.bmi}` : ''}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        display: {
                                          xs: "none",
                                          md: "table-cell",
                                          textAlign: "center",
                                        },
                                      }}
                                    >
                                      {item.caseNumber ? `${item?.caseNumber}` : ''}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title="More" placement="top">
                                        <IconButton
                                          onClick={(e: any) => {
                                            handleOpenMore(e, item?.memberPhysicalGrowthId || 0);
                                          }}
                                        >
                                          <MoreVertRoundedIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {resultData?.memberPhysicalGrowthColl?.length <= 0 && (
                              <TableRow>
                                <TableCell align="center" colSpan={8}>
                                  No record(s) found
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} paddingTop={1}>
                  {resultData?.memberPhysicalGrowthColl?.length > 0 && (
                    <Box className="pagination">
                      <Box className="pagination-label">
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-pagination-label"
                        >
                          {pageStartIndex} - {pageEndIndex} of {totalCount}
                        </SkeletonWrapper>
                      </Box>
                      <Box className="pagination-stack">
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-pagination-stack"
                        >
                          <Stack component="div">
                            <Pagination
                              color="primary"
                              count={counData} // NUMBER OF PAGES
                              page={Curr_page} // CURRENT PAGE
                              onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                            />
                          </Stack>
                        </SkeletonWrapper>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Popover
            elevation={1}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMore}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            {opd?.isEditable && <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleUpdate(memberPhysicalGrowthId);
                  }}
                >
                  <ListItemText primary="Edit" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  className="delete-container"
                  onClick={() => {
                    handleDeleteDilog(true);
                  }}
                >
                  Delete
                </ListItemButton>
              </ListItem>
            </List>}
          </Popover>

          <Dialog
            open={openDeleteDilog}
            onClose={() => {
              handleDeleteDilog(false);
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this growth chart item?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  deleteRow(memberPhysicalGrowthId);
                }}
                className="button-delete"
              >
                Yes
              </Button>
              <Button
                onClick={(e) => {
                  handleDeleteDilog(false);
                }}
                autoFocus
              >
                No
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Dialog */}
          <Dialog
            className="dialogPage"
            open={openAddDialog.open}
            onClose={handleCloseAddDialog}
            fullScreen
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: {
                width: "800px",
                height: "400px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Graph Details
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <GrowthChartItemSave
                opd={opd}
                isForPopUp={isForPopUp}
                memberPhysicalGrowthId={openAddDialog.memberPhysicalGrowthId}
                handleClosePopUp={() => { handleCloseAddDialog(); }}
              />
            </DialogContent>
          </Dialog>
        </>
      );
    }
  )
)

export default GrowthChartItemList
